<script lang="ts">
  import { onDestroy, onMount, tick, type Snippet } from 'svelte';
  import { t } from '../i18n';
  import { hudHidden, panelExpanded, panelInsets } from '../state/stores';
  import Icon from './icons/Icon.svelte';

  // The detail panel's container (S3-16, D15). Phones: a bottom sheet above the dock with three snap points — peek
  // (name and Follow), half and full — dragged by its handle, closed with ✕, Esc or a swipe down from peek. From
  // 900 px wide: a 380 px side panel on the right. It is a non-modal, labelled dialog: focus moves into it when it
  // opens and back to where it was when it closes. Its size is reported as a view inset, so the HUD and the camera
  // framing make room for it (as for the title card in Sprint 2).
  let {
    labelledBy,
    onclose,
    header,
    children,
  }: {
    /** Id of the heading that names the dialog. */
    labelledBy: string;
    onclose: () => void;
    header: Snippet;
    children: Snippet;
  } = $props();

  type Snap = 'peek' | 'half' | 'full';
  let snap = $state<Snap>('peek');
  let wide = $state(false);
  let sheet: HTMLElement;
  let height = $state(0);
  let width = $state(0);
  /** Live offset while dragging the handle (px, positive = down). */
  let dragDy = $state(0);
  let dragging = $state(false);
  let returnFocus: HTMLElement | null = null;

  const media = typeof matchMedia === 'function' ? matchMedia('(min-width: 900px)') : null;
  const onMedia = (): void => {
    wide = media?.matches ?? false;
  };

  onMount(() => {
    onMedia();
    media?.addEventListener('change', onMedia);
    returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // move focus into the panel (its heading), without scrolling the page — after the tap that opened it has
    // finished (a touch tap's compatibility mouse events would otherwise blur it again)
    void tick().then(() => setTimeout(() => document.getElementById(labelledBy)?.focus({ preventScroll: true }), 120));
  });
  onDestroy(() => {
    media?.removeEventListener('change', onMedia);
    panelInsets.set({ right: 0, bottom: 0 });
    panelExpanded.set(false);
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  });

  // report the space the panel takes (side panel: its width; sheet: its height above the dock)
  $effect(() => {
    panelInsets.set(wide ? { right: width, bottom: 0 } : { right: 0, bottom: height });
  });
  // a sheet opened beyond its peek is being read: it keeps the HUD on screen (S4-15)
  $effect(() => {
    panelExpanded.set(!wide && snap !== 'peek');
  });

  const ORDER: Snap[] = ['peek', 'half', 'full'];

  let start: { y: number; time: number } | null = null;
  function onHandleDown(e: PointerEvent): void {
    start = { y: e.clientY, time: e.timeStamp };
    dragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onHandleMove(e: PointerEvent): void {
    if (!start) return;
    dragDy = e.clientY - start.y;
  }
  function onHandleUp(e: PointerEvent): void {
    if (!start) return;
    const dy = e.clientY - start.y;
    const fast = Math.abs(dy) / Math.max(e.timeStamp - start.time, 1) > 0.5;
    start = null;
    dragging = false;
    dragDy = 0;
    if (Math.abs(dy) < 6) return; // a tap: handled by click
    const i = ORDER.indexOf(snap);
    const steps = fast ? Math.sign(dy) : Math.round(dy / 160) || Math.sign(dy);
    const next = i - steps;
    if (next < 0) onclose();
    else snap = ORDER[Math.min(next, ORDER.length - 1)]!;
    suppressClick = true;
    setTimeout(() => (suppressClick = false), 0);
  }
  let suppressClick = false;
  function onHandleClick(): void {
    if (suppressClick) return;
    snap = snap === 'peek' ? 'half' : snap === 'half' ? 'full' : 'peek';
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    onclose();
  }
</script>

<div
  bind:this={sheet}
  class="sheet panel {wide ? 'side' : snap}"
  class:wide
  class:dragging
  class:hud-hidden={$hudHidden}
  role="dialog"
  aria-modal="false"
  aria-labelledby={labelledBy}
  tabindex="-1"
  data-testid="detail-panel"
  data-snap={wide ? 'side' : snap}
  style:--drag="{dragDy}px"
  bind:offsetHeight={height}
  bind:offsetWidth={width}
  onkeydown={onKeydown}
>
  {#if !wide}
    <button
      type="button"
      class="handle"
      aria-label={snap === 'full' ? $t('panel.collapse') : $t('panel.expand')}
      onpointerdown={onHandleDown}
      onpointermove={onHandleMove}
      onpointerup={onHandleUp}
      onpointercancel={onHandleUp}
      onclick={onHandleClick}
    >
      <span class="grip" aria-hidden="true"></span>
    </button>
  {/if}
  <div class="head">
    {@render header()}
    <button type="button" class="close" aria-label={$t('panel.close')} onclick={onclose}>
      <Icon name="close" size={18} />
    </button>
  </div>
  <div class="body">
    {@render children()}
  </div>
</div>

<style>
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: var(--dock-h, 0px);
    z-index: 5;
    display: flex;
    flex-direction: column;
    padding: 0 0 calc(env(safe-area-inset-bottom) * 0);
    box-sizing: border-box;
    transform: translateY(max(0px, var(--drag)));
    transition:
      height var(--dur) var(--ease),
      transform var(--dur) var(--ease),
      opacity var(--dur) var(--ease);
    outline: none;
  }
  .sheet.dragging {
    transition: none;
  }
  /* peek: just the handle and the header (name, status, Follow) */
  .sheet.peek {
    height: auto;
  }
  .sheet.peek .body {
    display: none;
  }
  .sheet.half {
    height: 50vh;
  }
  .sheet.full {
    height: calc(100vh - var(--hud-top) - var(--dock-h, 0px) - 8px);
  }
  .sheet.wide {
    left: auto;
    top: var(--hud-top);
    bottom: 0;
    width: 380px;
    height: auto;
    transform: none;
    border-right: none;
    border-bottom: none;
  }
  .handle {
    flex: none;
    height: 24px;
    display: grid;
    place-items: center;
    cursor: grab;
    touch-action: none;
  }
  /* 44 px touch target */
  .handle {
    position: relative;
  }
  .handle::after {
    content: '';
    position: absolute;
    inset: -10px 0;
  }
  .grip {
    width: 44px;
    height: 4px;
    background: var(--rule);
  }
  .head {
    flex: none;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 0 6px 0 14px;
  }
  .sheet.wide .head {
    padding-top: 10px;
  }
  .close {
    flex: none;
    margin-left: auto;
    width: var(--touch);
    height: var(--touch);
    display: grid;
    place-items: center;
    color: var(--fg-muted);
  }
  .close:hover {
    color: var(--accent);
  }
  .body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 4px 14px 14px;
  }
  /* auto-hidden with the HUD (S4-15): the peek slides below the screen, the side panel to the right */
  .sheet.hud-hidden {
    transform: translateY(calc(100% + var(--dock-h, 0px) + 16px));
    opacity: 0;
    pointer-events: none;
  }
  .sheet.wide.hud-hidden {
    transform: translateX(100%);
  }
  @media (prefers-reduced-motion: reduce) {
    .sheet {
      transition: none;
    }
    .sheet.hud-hidden,
    .sheet.wide.hud-hidden {
      transform: none;
      transition: opacity 300ms linear;
    }
  }
  :global(html[data-reduced-motion]) .sheet.hud-hidden {
    transform: none;
    transition: opacity 300ms linear;
  }
  /* phones: a slimmer handle (its touch target stays 44 px) */
  @media (max-width: 899px) {
    .handle {
      height: 12px;
    }
    .handle::after {
      inset: -16px 0;
    }
  }
</style>
